import { useCallback, useState } from 'react';
import { ChatSidebar, type ChatMessage } from '../../components/DemoDock/ChatSidebar';
import { PreviewFrame } from '../../components/DemoDock/PreviewFrame';
import { SplitScreenLayout } from '../../components/DemoDock/SplitScreen';
import { useDemoDockCredits } from '../../components/DemoDock/Credits/credits';
import { generateFeature } from '../../components/DemoDock/Models/modelApi';
import { useDemoDockAuth } from '../../components/DemoDock/auth';
import type { EditablePageElement, ReplaceTextCommand } from '../../components/DemoDock/processPrompt';
import './DemoDock.css';

let messageId = 0;

function nextId() {
  messageId += 1;
  return `m-${messageId}`;
}

function DemoDock() {
  const [targetUrl, setTargetUrl] = useState('https://example.com');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pageContext, setPageContext] = useState<EditablePageElement[]>([]);
  const [outboundCommand, setOutboundCommand] = useState<ReplaceTextCommand | null>(null);
  const credits = useDemoDockCredits();
  const auth = useDemoDockAuth();

  const handleSend = useCallback(async (text: string) => {
    setMessages((prev) => [...prev, { id: nextId(), role: 'user', text }]);

    if (!credits.hasCredits) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          text: 'You are out of Demo Dock credits for this month. Upgrade your plan to keep running demo commands.',
        },
      ]);
      return;
    }

    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'assistant', text: 'Generating with the private model layer...' },
    ]);

    try {
      if (pageContext.length === 0) {
        throw new Error('The preview has not exposed any editable text yet. Wait for it to load and try again.');
      }

      const response = await generateFeature(text, pageContext);
      credits.setSummary(response);

      for (const command of response.commands) {
        setOutboundCommand({ ...command });
      }

      setMessages((prev) => [
        ...prev.slice(0, -1),
        { id: nextId(), role: 'assistant', text: response.data.trim() || 'The model returned an empty response.' },
      ]);
    } catch (error) {
      console.log('generate feature ERROR:', error);
      const message = error instanceof Error ? error.message : 'Demo Dock could not reach the AI model service.';
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          id: nextId(),
          role: 'assistant',
          text: `${message} No credit was deducted.`,
        },
      ]);
    }
  }, [credits, pageContext]);

  return (
    <SplitScreenLayout
      sidebar={<ChatSidebar auth={auth} credits={credits} messages={messages} onSend={handleSend} />}
      main={
        <PreviewFrame
          auth={auth}
          targetUrl={targetUrl}
          onTargetUrlChange={setTargetUrl}
          outboundCommand={outboundCommand}
          onPageContextChange={setPageContext}
        />
      }
    />
  );
}

export default DemoDock;
